import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// Fix L (2026-09-25): politica de privacidad publica. Meta la exige para
// publicar la app de WhatsApp, y la seccion "eliminacion-de-datos" es la URL de
// instrucciones de eliminacion de datos que pide la configuracion de la app.
// Pagina estatica: no llama a la API ni depende de la sesion.

const CLINIC = {
  name: "Strani Just Because",
  address: "6430 Richmond Avenue, Suite 270, Houston, Texas 77057",
  phone: "+1 713 493-5555",
};

const CONTENT = {
  es: {
    title: "Política de privacidad",
    updated: "Última actualización: 25 de septiembre de 2026",
    intro: `En ${CLINIC.name} cuidamos la información que nos confiás. Esta política explica qué datos recopilamos, para qué los usamos, con quién los compartimos y cómo podés ejercer tus derechos sobre ellos.`,
    sections: [
      {
        id: "quienes-somos",
        title: "Quiénes somos",
        body: [`${CLINIC.name} es una clínica de medicina estética ubicada en ${CLINIC.address}. Podés contactarnos al ${CLINIC.phone}, por teléfono o por WhatsApp.`],
      },
      {
        id: "datos",
        title: "Qué datos recopilamos",
        list: [
          "Datos de contacto: nombre, teléfono y correo electrónico.",
          "Datos de tus turnos: fechas, horarios, tratamientos solicitados y profesional asignado.",
          "Información clínica y de tratamientos que registramos durante tu atención, incluidos consentimientos y fotografías de seguimiento cuando corresponda.",
          "Mensajes que intercambiás con la clínica por WhatsApp.",
          "Datos de pagos: los pagos con tarjeta los procesa Square. No almacenamos números completos de tarjeta.",
        ],
      },
      {
        id: "uso",
        title: "Para qué usamos tus datos",
        list: [
          "Agendar, confirmar, reprogramar y recordarte tus turnos.",
          "Brindarte los tratamientos y hacer su seguimiento.",
          "Responder tus consultas, incluso fuera del horario de atención.",
          "Procesar pagos y emitir comprobantes.",
          "Cumplir con las obligaciones legales aplicables a una clínica.",
        ],
      },
      {
        id: "whatsapp",
        title: "Mensajes por WhatsApp y asistente automático",
        body: [
          "Cuando nos escribís por WhatsApp, tus mensajes se transmiten a través de la plataforma WhatsApp Business de Meta.",
          "Algunas respuestas las da un asistente automático que usa inteligencia artificial para contestar consultas frecuentes y ayudarte a reservar turnos. En cualquier momento podés pedir que te atienda una persona del equipo.",
          "Solo te enviamos mensajes relacionados con tus turnos, tratamientos y consultas. Si no querés recibir más mensajes, respondé indicándolo y dejaremos de enviarlos.",
        ],
      },
      {
        id: "compartir",
        title: "Con quién compartimos tus datos",
        body: [
          "No vendemos ni alquilamos tu información personal.",
          "Solo la compartimos con proveedores que necesitamos para operar, y únicamente en la medida necesaria: el servicio de alojamiento de nuestra plataforma, Meta (WhatsApp), Square (pagos) y el proveedor de inteligencia artificial que procesa el texto de los mensajes para generar respuestas. También podemos compartirla cuando la ley nos lo exija.",
        ],
      },
      {
        id: "conservacion",
        title: "Cuánto tiempo conservamos tus datos",
        body: ["Conservamos tus datos mientras seas paciente de la clínica y durante el tiempo que exijan las normas aplicables a los registros médicos. Pasado ese plazo, los eliminamos o anonimizamos."],
      },
      {
        id: "seguridad",
        title: "Seguridad",
        body: ["Protegemos tu información con acceso restringido al personal autorizado, conexiones cifradas y proveedores con estándares de seguridad reconocidos."],
      },
      {
        id: "derechos",
        title: "Tus derechos",
        body: ["Podés pedirnos acceder a tus datos, corregirlos, eliminarlos o dejar de recibir mensajes. Para hacerlo, escribinos por WhatsApp o llamanos al número de contacto."],
      },
      {
        id: "eliminacion-de-datos",
        title: "Cómo solicitar la eliminación de tus datos",
        list: [
          `Envianos un mensaje por WhatsApp o llamanos al ${CLINIC.phone} indicando tu nombre completo y que querés eliminar tus datos.`,
          "Para proteger tu información, vamos a confirmar tu identidad antes de procesar el pedido.",
          "Eliminaremos tus datos personales y tu historial de mensajes dentro de los 30 días siguientes y te confirmaremos cuando esté hecho.",
          "La información clínica que la ley nos obliga a conservar se mantendrá solo durante el plazo exigido, y después se eliminará.",
        ],
      },
      {
        id: "menores",
        title: "Menores de edad",
        body: ["Nuestros servicios están dirigidos a personas mayores de 18 años. La atención de menores requiere la autorización de su madre, padre o tutor."],
      },
      {
        id: "cambios",
        title: "Cambios en esta política",
        body: ["Podemos actualizar esta política. Publicaremos cualquier cambio en esta misma página con su fecha de actualización."],
      },
      {
        id: "contacto",
        title: "Contacto",
        body: [`${CLINIC.name} · ${CLINIC.address} · ${CLINIC.phone}`],
      },
    ],
    back: "Volver al inicio",
  },
  en: {
    title: "Privacy Policy",
    updated: "Last updated: September 25, 2026",
    intro: `At ${CLINIC.name} we care about the information you share with us. This policy explains what data we collect, how we use it, who we share it with, and how you can exercise your rights over it.`,
    sections: [
      {
        id: "who-we-are",
        title: "Who we are",
        body: [`${CLINIC.name} is an aesthetic medicine clinic located at ${CLINIC.address}. You can reach us at ${CLINIC.phone}, by phone or WhatsApp.`],
      },
      {
        id: "data",
        title: "What data we collect",
        list: [
          "Contact details: name, phone number and email address.",
          "Appointment details: dates, times, requested treatments and assigned professional.",
          "Clinical and treatment information recorded during your care, including consent forms and follow-up photos when applicable.",
          "Messages you exchange with the clinic on WhatsApp.",
          "Payment details: card payments are processed by Square. We do not store full card numbers.",
        ],
      },
      {
        id: "use",
        title: "How we use your data",
        list: [
          "To schedule, confirm, reschedule and remind you of your appointments.",
          "To provide your treatments and follow up on them.",
          "To answer your questions, including outside business hours.",
          "To process payments and issue receipts.",
          "To comply with the legal obligations that apply to a medical clinic.",
        ],
      },
      {
        id: "whatsapp",
        title: "WhatsApp messages and automated assistant",
        body: [
          "When you message us on WhatsApp, your messages are transmitted through Meta's WhatsApp Business Platform.",
          "Some replies are provided by an automated assistant that uses artificial intelligence to answer frequent questions and help you book appointments. You can ask to speak with a member of our team at any time.",
          "We only send you messages related to your appointments, treatments and inquiries. If you no longer want to receive messages, reply letting us know and we will stop.",
        ],
      },
      {
        id: "sharing",
        title: "Who we share your data with",
        body: [
          "We do not sell or rent your personal information.",
          "We only share it with providers we need in order to operate, and only to the extent necessary: our platform hosting service, Meta (WhatsApp), Square (payments) and the artificial intelligence provider that processes message text to generate replies. We may also share it when required by law.",
        ],
      },
      {
        id: "retention",
        title: "How long we keep your data",
        body: ["We keep your data while you are a patient of the clinic and for as long as required by the rules that apply to medical records. After that period, we delete or anonymize it."],
      },
      {
        id: "security",
        title: "Security",
        body: ["We protect your information with access restricted to authorized staff, encrypted connections and providers with recognized security standards."],
      },
      {
        id: "rights",
        title: "Your rights",
        body: ["You can ask us to access, correct or delete your data, or to stop sending you messages. To do so, message us on WhatsApp or call our contact number."],
      },
      {
        id: "eliminacion-de-datos",
        title: "How to request deletion of your data",
        list: [
          `Send us a WhatsApp message or call us at ${CLINIC.phone} with your full name, stating that you want your data deleted.`,
          "To protect your information, we will verify your identity before processing the request.",
          "We will delete your personal data and message history within 30 days and confirm once it is done.",
          "Clinical information we are legally required to keep will be retained only for the required period and deleted afterwards.",
        ],
      },
      {
        id: "minors",
        title: "Minors",
        body: ["Our services are intended for people over 18. Care for minors requires authorization from a parent or legal guardian."],
      },
      {
        id: "changes",
        title: "Changes to this policy",
        body: ["We may update this policy. Any changes will be posted on this page with their update date."],
      },
      {
        id: "contact",
        title: "Contact",
        body: [`${CLINIC.name} · ${CLINIC.address} · ${CLINIC.phone}`],
      },
    ],
    back: "Back to home",
  },
};

export default function PrivacyPage() {
  const location = useLocation();
  const [lang, setLang] = useState(location.pathname.startsWith("/privacy") ? "en" : "es");
  const c = CONTENT[lang];

  useEffect(() => {
    document.title = `${c.title} · ${CLINIC.name}`;
  }, [c.title]);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView();
    }
  }, [location.hash, lang]);

  return (
    <div className="min-h-screen bg-[#F7F3ED] text-[#292522]">
      <div className="max-w-3xl mx-auto px-5 py-12">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="font-serif text-2xl tracking-wide text-[#B99A62]">STRANI</Link>
          <div className="flex gap-1 text-sm">
            {["es", "en"].map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded ${lang === l ? "bg-[#292522] text-[#F7F3ED]" : "text-[#6B6058] hover:bg-black/5"}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <h1 className="font-serif text-4xl mb-2">{c.title}</h1>
        <p className="text-sm text-[#6B6058] mb-8">{c.updated}</p>
        <p className="leading-relaxed mb-10">{c.intro}</p>

        {c.sections.map((s) => (
          <section key={s.id} id={s.id} className="mb-8 scroll-mt-6">
            <h2 className="font-serif text-2xl mb-3">{s.title}</h2>
            {s.body?.map((p, i) => (
              <p key={i} className="leading-relaxed mb-3">{p}</p>
            ))}
            {s.list && (
              <ul className="list-disc pl-5 space-y-2 leading-relaxed">
                {s.list.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            )}
          </section>
        ))}

        <div className="mt-12 pt-6 border-t border-black/10">
          <Link to="/" className="text-sm text-[#6B6058] hover:text-[#292522]">← {c.back}</Link>
        </div>
      </div>
    </div>
  );
}
